import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { Close as CloseIcon, Event as EventIcon } from '@mui/icons-material';
import { Timestamp, serverTimestamp } from 'firebase/firestore';
import GooglePlacesSearch from '../common/GooglePlacesSearch';
import { useUser } from '../../contexts/UserContext';

export interface EventCreationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (eventId: string) => void;
}

const EventCreationDialog: React.FC<EventCreationDialogProps> = ({ open, onClose, onSuccess }) => {
  const { currentUser } = useUser();
  const [eventSaveError, setEventSaveError] = useState('');
  const [newEvent, setNewEvent] = useState({
    title: '',
    eventType: 'personal',
    startDate: new Date(),
    endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
    location: {
      address: '',
      name: '',
      coordinates: [0, 0],
    },
    description: '',
  });

  const [savingEvent, setSavingEvent] = useState(false);

  const createEvent = async () => {
    if (!currentUser) return;
    if (!newEvent.title.trim()) {
      setEventSaveError('Event title is required');
      return;
    }

    setSavingEvent(true);
    setEventSaveError('');

    try {
      // Use backend API to create event with proper members array
      const { createApiUrl } = await import('../../utils/apiConfig');
      const { getAuthHeaders } = await import('../../utils/auth');
      const apiUrl = createApiUrl('/api/events');
      const headers = await getAuthHeaders();

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: newEvent.title.trim(),
          description: newEvent.description?.trim(),
          startDate: newEvent.startDate.toISOString(),
          endDate: newEvent.endDate.toISOString(),
          location: newEvent.location,
          eventType: newEvent.eventType,
          isPublic: false,
          userId: currentUser.uid,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create event');
      }

      const eventId = result.eventId;

      // Mirror Firestore structure locally (if ever needed)
      const eventData = {
        title: newEvent.title.trim(),
        startDate: Timestamp.fromDate(newEvent.startDate),
        endDate: Timestamp.fromDate(newEvent.endDate),
        location: newEvent.location,
        description: newEvent.description?.trim(),
        eventType: newEvent.eventType,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isPublic: false,
        owner: currentUser?.uid,
        userId: currentUser.uid,
        isRecurring: false,
        photoCount: 0,
        participants: [
          {
            userId: currentUser.uid,
            email: currentUser.email || '',
            displayName: currentUser.displayName || 'Event Owner',
            photoURL: currentUser.photoURL || '',
            role: 'owner',
            status: 'accepted',
            invitedAt: new Date().toISOString(),
            invitedBy: currentUser.uid,
            acceptedAt: new Date().toISOString(),
          },
        ],
      };

      // Return newly created id to parent
      if (onSuccess) {
        onSuccess(eventId);
      }
      if (onClose) {
        onClose();
      }

      return eventData;
    } catch (error) {
      console.error('Error creating event:', error);
      setEventSaveError('Failed to create event. Please try again.');
    } finally {
      setSavingEvent(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='h6'>
            <EventIcon color='secondary' sx={{ mr: 1, verticalAlign: 'middle' }} />
            Add New Event
          </Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {eventSaveError && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {eventSaveError}
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label='Event Title'
              fullWidth
              required
              value={newEvent.title}
              onChange={(e) => setNewEvent((prev) => ({ ...prev, title: e.target.value }))}
              disabled={savingEvent}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id='event-type-label'>Event Type</InputLabel>
              <Select
                labelId='event-type-label'
                value={newEvent.eventType}
                label='Event Type'
                onChange={(e) => setNewEvent((prev) => ({ ...prev, eventType: e.target.value }))}
                disabled={savingEvent}
              >
                <MenuItem value='personal'>Personal</MenuItem>
                <MenuItem value='business'>Business</MenuItem>
                <MenuItem value='vacation'>Vacation Activity</MenuItem>
                <MenuItem value='reminder'>Reminder</MenuItem>
                <MenuItem value='social'>Social</MenuItem>
                <MenuItem value='other'>Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ width: '100%' }}>
                <DateTimePicker<Date>
                  label='Start Date & Time'
                  value={newEvent.startDate}
                  onChange={(newValue: Date | null) => {
                    if (newValue) {
                      setNewEvent((prev) => {
                        const updatedEndDate =
                          newValue > prev.endDate
                            ? new Date(newValue.getTime() + 2 * 60 * 60 * 1000)
                            : prev.endDate;
                        return {
                          ...prev,
                          startDate: newValue,
                          endDate: updatedEndDate,
                        };
                      });
                    }
                  }}
                  disabled={savingEvent}
                  renderInput={(props) => <TextField {...props} fullWidth />}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ width: '100%' }}>
                <DateTimePicker<Date>
                  label='End Date & Time'
                  value={newEvent.endDate}
                  onChange={(newValue: Date | null) => {
                    if (newValue) {
                      setNewEvent((prev) => ({ ...prev, endDate: newValue }));
                    }
                  }}
                  disabled={savingEvent}
                  minDate={newEvent.startDate}
                  renderInput={(props) => <TextField {...props} fullWidth />}
                />
              </Box>
            </Grid>
          </LocalizationProvider>

          <Grid item xs={12}>
            <GooglePlacesSearch
              value={newEvent.location?.address || ''}
              searchType='places'
              label='Location'
              fullWidth
              onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                setNewEvent((prev) => ({
                  ...prev,
                  location: {
                    address: place_name,
                    name: place_name,
                    coordinates: [lat, lng],
                  },
                }));
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label='Description'
              fullWidth
              multiline
              rows={3}
              value={newEvent.description || ''}
              onChange={(e) => setNewEvent((prev) => ({ ...prev, description: e.target.value }))}
              disabled={savingEvent}
              placeholder='Add details about this event...'
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={savingEvent}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='secondary'
          onClick={createEvent}
          disabled={savingEvent}
          startIcon={savingEvent ? <CircularProgress size={20} /> : <EventIcon />}
        >
          {savingEvent ? 'Saving...' : 'Create Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventCreationDialog;

